import { createContext, useContext, useRef, useState } from "react";
import { Feature, MapContextType, MapMarker } from "./types";
import { GeoUtils } from "@/migrated-widgets/table-map/utils/geo";

const initialValue: MapContextType = {
    setMap: () => { },
    setMarkers: () => { },
    addMarker: () => { },
    removeMarker: () => { },
    centerBy: () => { },
    resetGeoData: () => { },
    setGeoData: () => { },
    setDataFunctions: () => { },
    map: undefined,
    markers: [],
    infoWindow: { current: null },
}

const MapContext = createContext<MapContextType>(initialValue);

const MapProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [map, setMap] = useState<MapContextType['map']>();
    const [markers, setMarkers] = useState<MapContextType['markers']>([]);
    const infoWindow = useRef<google.maps.InfoWindow>(null);
    const lastOpen = useRef<number>(Date.now());

    const addMarker = (marker: any, preventCollision: boolean = false) => {

        setMarkers(prev => {
            const markersToRemove = prev.filter(m => m.id === marker.id);

            if (preventCollision) {
                const collisioningMarker = prev.find((__marker) => marker.id !== __marker.id && GeoUtils.collisionDetection(__marker, marker, .1));
                const newPosition = GeoUtils.relocateMarkerPosition(marker, .1)
                if (collisioningMarker) {
                    marker.marker.position = newPosition
                }
            }
            markersToRemove.forEach(m => m.remove())
            return [
                ...prev.filter(m => m.id !== marker.id),
                marker
            ]
        })
    }

    const removeMarker = (id: MapMarker['id']) => {
        setMarkers(prev => {
            const _markers = prev.filter(m => m.id === id);

            _markers.forEach(m => m.remove());

            return prev.filter(m => m.id !== id)
        })
    }

    const centerBy = (positions: (google.maps.LatLngLiteral | google.maps.LatLngBounds)[], zoom?: number) => {
        if (map) {
            const bounds = new google.maps.LatLngBounds();

            positions.forEach((position) => {
                if (position instanceof google.maps.LatLngBounds) bounds.union(position); else bounds.extend(position);
            })

            map.fitBounds(bounds)
            if (zoom !== undefined) {
                google.maps.event.addListenerOnce(map, 'idle', () => {
                    map.setZoom(zoom);
                });
            }
        }
    }

    const resetGeoData = () => {
        if (map) {
            map.data.forEach(function (feature) {
                map.data.remove(feature);
            });
        }

    }
    const setGeoData = (geoData: Feature[]) => {
        if (map) {
            resetGeoData();
            geoData.forEach((geoJson) => {
                map.data.addGeoJson(geoJson);
            })
        }
    }

    const setDataFunctions = () => {
        if (map) {
            map.data.setStyle((feature) => {
                return /** @type {!google.maps.Data.StyleOptions} */ {
                    strokeColor: feature.getProperty("strokeColor") as string ?? "gray",
                    strokeOpacity: feature.getProperty("strokeOpacity") as number ?? 1,
                    strokeWeight: feature.getProperty("strokeWeight") as number ?? 1,
                    fillColor: feature.getProperty("fillColor") as string ?? "gray",
                    fillOpacity: feature.getProperty("fillOpacity") as number ?? 1,
                    zIndex: feature.getProperty("zIndex") as number ?? 1,
                };
            })
            map.data.addListener('mouseover', (event: any) => {
                if (lastOpen.current - Date.now() < 400) {
                    if (infoWindow) {
                        infoWindow.current?.close();
                        infoWindow.current = null;
                    }
                    infoWindow.current = new google.maps.InfoWindow({
                        content: event.feature.getProperty("label") ?? "-",
                        position: event.latLng,
                        pixelOffset: new google.maps.Size(0, -5),
                        zIndex: event.feature.getProperty("zIndex") ?? 1,
                    });
                    infoWindow.current.open(map);
                }
                lastOpen.current = Date.now();
            })
            map.data.addListener("mouseout", () => {
                infoWindow.current?.close();
            });
        }
    }

    return (
        <MapContext.Provider value={{
            map,
            setMap,
            markers,
            setMarkers,
            addMarker,
            removeMarker,
            centerBy,
            resetGeoData,
            setGeoData,
            setDataFunctions,
            infoWindow
        }}>
            {children}
        </MapContext.Provider>
    )
}


const useMap = () => {
    return useContext(MapContext)
}

export { MapContext, MapProvider, useMap }